import {
	DecoratorNode,
	type LexicalNode,
	type NodeKey,
	type SerializedLexicalNode
} from 'lexical';
import type { JSX } from 'react';
import { DiffHighlight } from '@/components/editor/components/highlight-difference-plugin/components/diff-highlight';

type SerializedDiffNode = SerializedLexicalNode & {
	type: 'diff-node';
	version: 1;
	text: string;
	added?: boolean;
	removed?: boolean;
};

function isSerializedDiffNode(
	node: SerializedLexicalNode
): node is SerializedDiffNode {
	const rec = node as Record<string, unknown>;
	return (
		rec.type === 'diff-node' && typeof rec.version === 'number' && 'text' in rec
	);
}

export class DiffNode extends DecoratorNode<JSX.Element> {
	__text: string;
	__added: boolean;
	__removed: boolean;

	static getType(): string {
		return 'diff-node';
	}

	static clone(node: DiffNode): DiffNode {
		return new DiffNode(node.__text, node.__added, node.__removed, node.__key);
	}

	constructor(text: string, added = false, removed = false, key?: NodeKey) {
		super(key);
		this.__text = text;
		this.__added = added;
		this.__removed = removed;
	}

	// Accept the broad base type and narrow inside
	static importJSON(serializedNode: SerializedLexicalNode): DiffNode {
		if (isSerializedDiffNode(serializedNode)) {
			return new DiffNode(
				serializedNode.text,
				Boolean(serializedNode.added),
				Boolean(serializedNode.removed)
			);
		}
		return new DiffNode('');
	}

	exportJSON(): SerializedDiffNode {
		return {
			type: 'diff-node',
			version: 1,
			text: this.__text,
			added: this.__added,
			removed: this.__removed
		};
	}

	createDOM(): HTMLElement {
		return document.createElement('span');
	}

	updateDOM(): boolean {
		return false;
	}

	decorate(): JSX.Element {
		return (
			<DiffHighlight
				added={this.__added}
				removed={this.__removed}
				text={this.__text}
			/>
		);
	}
}

// Helpers (optional but handy)
export function $createDiffNode(
	text: string,
	added = false,
	removed = false
): DiffNode {
	return new DiffNode(text, added, removed);
}
export function $isDiffNode(node?: LexicalNode | null): node is DiffNode {
	return node instanceof DiffNode;
}
